export interface StrategyDescription {
  label: string;
  description: string;
  overview: string;
}

/**
 * Human-readable summaries for the strategy identifiers stored on positions
 * and returned by the strategy status endpoints.
 */
export const STRATEGY_DESCRIPTIONS: Record<string, StrategyDescription> = {
  ON: {
    label: 'Overnight',
    description: 'Overnight trend and carry strategy using the configured ETF signals.',
    overview: 'Uses the overnight session windows and longer-term moving-average context to manage the configured ETF positions.',
  },
  ORB: {
    label: 'Opening Range Breakout',
    description: 'Intraday opening-range breakout strategy with ATR and momentum checks.',
    overview: 'Evaluates the opening range, ATR-based momentum, and configured exit rules before managing an intraday breakout position.',
  },
  DT: {
    label: 'ATR Day Trade',
    description: 'Multi-symbol intraday strategy driven by ATR thresholds and entry windows.',
    overview: 'Uses configured intraday ATR thresholds and entry windows across the selected symbols, with risk controls and end-of-day exits.',
  },
  NQMM: {
    label: 'Nasdaq-100 Monthly Momentum',
    description: 'Long-only monthly momentum sleeve for the current Nasdaq-100 universe.',
    overview: 'Ranks the current Nasdaq-100 universe, sizes the selected holdings using the configured method, and prepares monthly rebalance actions.',
  },
  MORB: {
    label: 'Managed ORB',
    description: 'Semi-automatic, user-selected opening-range breakout manager.',
    overview: 'Tracks selected stocks, checks volume and ATR quality gates, and manages entries, staged stops, and T+3 reductions.',
  },
  MANUAL: {
    label: 'Manual',
    description: 'Position placed manually outside an automated strategy.',
    overview: 'This position is not currently owned by an automated strategy.',
  },
  UNASSIGNED: {
    label: 'Unassigned',
    description: 'No strategy attribution is available for this position.',
    overview: 'No strategy description is available for this position.',
  },
};

export const getStrategyDescription = (
  strategy?: string | null,
): StrategyDescription => {
  const key = String(strategy || '').trim().toUpperCase();
  return STRATEGY_DESCRIPTIONS[key] || {
    label: key || 'Unassigned',
    description: 'Strategy attribution is not documented.',
    overview: 'No strategy description is available for this strategy.',
  };
};
